import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";
import {
  Settings,
  Clock,
  Bell,
  Save,
  RotateCcw,
  Users as UsersIcon
} from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "../utils/api";
import CatalogoServiziPanel from "../components/CatalogoServiziPanel";
import { Badge } from "../components/designSystem";

// Offset Task e Risorse Assegnate sono sostituite dalle task di ogni servizio
// (Catalogo servizi → Task e risorse). Restano in sola lettura perché valgono
// ancora per i clienti senza un servizio collegato; si eliminano quando tutti
// i clienti ne avranno uno.
function AvvisoObsoleto({ testId }) {
  return (
    <div className="mt-3 px-3 py-2.5 rounded-[10px] bg-[#f3f3f8] border border-[#e6e6f0] text-xs leading-relaxed text-[#565d6d]" data-testid={testId}>
      Tempi e risorse ora si configurano in ogni servizio, in <strong>Catalogo servizi → Task e risorse</strong>.
      Questi valori restano solo per i clienti senza un servizio collegato e non sono più modificabili.
    </div>
  );
}

const DEFAULT_SETTINGS = {
  offset_script_cliente: -5,
  offset_ped_programmazione: 2,
  offset_avvio_adv: 3,
  offset_appuntamento_successivo: 30,
  enable_appuntamento_successivo: true,
  offset_editing_video: -5,
  soglia_giallo: 3,
  enable_reminder_scaduto: true,
  task_role_visibility: {
    editing_video: ["editor"],
    script_cliente: ["smm", "videomaker"],
    ped_programmazione: ["smm"],
    avvio_adv: ["smm"],
    appuntamento_successivo: ["smm"],
    report_fine_mese: ["smm"],
  }
};

const OFFSET_LABELS = {
  offset_script_cliente: "Script al Cliente",
  offset_ped_programmazione: "PED + Programmazione",
  offset_avvio_adv: "Avvio Sponsorizzate / ADV",
  offset_appuntamento_successivo: "Appuntamento Mese Successivo",
  offset_editing_video: "Editing Video"
};

// Mappa task_id -> label per la sezione visibilità ruoli
const TASK_VISIBILITY_LABELS = {
  script_cliente: "Script al Cliente",
  ped_programmazione: "PED + Programmazione",
  avvio_adv: "Avvio Sponsorizzate / ADV",
  appuntamento_successivo: "Appuntamento Mese Successivo",
  report_fine_mese: "Report di Fine Mese",
  editing_video: "Editing Video"
};

const ROLES_OPTIONS = [
  { id: "smm", label: "SMM" },
  { id: "videomaker", label: "Content Creator" },
  { id: "editor", label: "Editor" },
];

export default function SettingsPage({ user }) {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const isDirettore = user?.role === "direttore_tecnico" || user?.role === "direttore_commerciale";
  const isPM = user?.role === "pm" || isDirettore || (user?.secondary_roles || []).includes("pm");
  const isAdmin = user?.email === "info@diecixcento.com" || user?.is_responsabile_operativo || isDirettore;
  const canAccess = isPM || isAdmin || user?.is_responsabile_operativo;

  // Catalogo Servizi: volutamente più stretto di isAdmin (esclude direttore_tecnico) —
  // la configurazione di prezzi e servizi offerti è competenza di amministrazione/direzione
  // commerciale, non tecnica. Rispecchia il controllo lato backend in routes/catalogo_servizi.py.
  const isDirettoreCommerciale = user?.role === "direttore_commerciale" || (user?.secondary_roles || []).includes("direttore_commerciale");
  const canManageCatalogoServizi = user?.email === "info@diecixcento.com" || user?.is_responsabile_operativo || isDirettoreCommerciale;

  useEffect(() => {
    if (!canAccess) {
      navigate("/dashboard");
      return;
    }
    fetchSettings();
  }, [canAccess, navigate]);

  const fetchSettings = async () => {
    try {
      const response = await apiFetch("/settings");
      if (response.ok) {
        const data = await response.json();
        setSettings({ ...DEFAULT_SETTINGS, ...data });
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await apiFetch("/settings", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast.success("Impostazioni salvate");
      } else {
        toast.error("Errore nel salvataggio");
      }
    } catch (error) {
      toast.error("Errore nel salvataggio delle impostazioni");
    } finally {
      setSaving(false);
    }
  };

  // Offset e risorse sono in sola lettura (AvvisoObsoleto): il ripristino
  // non deve cambiarli di nascosto, valgono ancora per i clienti senza servizio.
  const handleReset = () => {
    setSettings(prev => ({
      ...DEFAULT_SETTINGS,
      ...Object.fromEntries(Object.keys(OFFSET_LABELS).map(k => [k, prev[k]])),
      enable_appuntamento_successivo: prev.enable_appuntamento_successivo,
      task_role_visibility: prev.task_role_visibility,
    }));
    toast.info("Impostazioni ripristinate ai valori predefiniti");
  };

  const updateOffset = (key, value) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue)) {
      setSettings(prev => ({ ...prev, [key]: numValue }));
    }
  };

  if (!canAccess) {
    return null;
  }

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-muted rounded w-48"></div>
        <div className="h-64 bg-muted rounded-xl"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-12 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-2 border-b border-slate-200/80">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
            Impostazioni
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Configura regole di generazione task, scadenze, visibilità ruoli e notifiche
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={handleReset}
            data-testid="reset-settings-btn"
            className="shadow-sm"
          >
            <RotateCcw className="w-4 h-4 mr-2 text-muted-foreground" />
            Ripristina
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm"
            data-testid="save-settings-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Salvataggio..." : "Salva impostazioni"}
          </Button>
        </div>
      </div>

      <div className="space-y-8">
        {/* Task Offsets */}
        <Card className="border border-slate-200 shadow-sm bg-slate-50 overflow-hidden grayscale opacity-70" data-testid="offsets-section" aria-disabled="true">
          <CardHeader className="p-6 pb-4 bg-slate-50/50 border-b border-slate-100">
            <CardTitle className="font-heading flex items-center gap-2.5 text-base sm:text-lg">
              <span className="p-2 rounded-lg bg-indigo-50 text-indigo-600 border border-indigo-100/80">
                <Clock className="w-4 h-4" />
              </span>
              Offset Task (giorni da Data Girato)
              <Badge testId="offsets-obsoleto">Obsoleto</Badge>
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm mt-1 text-slate-500">
              Configura quanti giorni prima (-) o dopo (+) la Data Girato deve essere posizionata la scadenza di ogni task.
            </CardDescription>
            <AvvisoObsoleto testId="offsets-avviso-obsoleto" />
          </CardHeader>
          <CardContent className="p-6">
            <div className="divide-y divide-slate-100 rounded-xl border border-slate-100 bg-slate-50/30 overflow-hidden">
              {Object.entries(OFFSET_LABELS).map(([key, label]) => (
                <div
                  key={key}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4"
                >
                  <div className="flex-1 pr-4">
                    <Label htmlFor={key} className="text-sm font-semibold text-slate-800 cursor-pointer">
                      {label}
                    </Label>
                    {key === "offset_appuntamento_successivo" ? (
                      <p className="text-xs text-muted-foreground mt-1">
                        Opzionale · Attiva o disattiva la generazione automatica
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {settings[key] < 0
                          ? `${Math.abs(settings[key])} giorni prima del girato`
                          : settings[key] === 0
                          ? "Stesso giorno del girato"
                          : `${settings[key]} giorni dopo il girato`}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 self-end sm:self-center shrink-0">
                    {key === "offset_appuntamento_successivo" && (
                      <div className="flex items-center gap-2 mr-2">
                        <Switch
                          disabled
                          checked={settings.enable_appuntamento_successivo}
                          onCheckedChange={(checked) => 
                            setSettings(prev => ({ ...prev, enable_appuntamento_successivo: checked }))
                          }
                          data-testid="toggle-appuntamento"
                        />
                        <span className="text-xs text-muted-foreground font-medium">
                          {settings.enable_appuntamento_successivo ? "Attivo" : "Disattivo"}
                        </span>
                      </div>
                    )}
                    <Input
                      id={key}
                      type="number"
                      value={settings[key]}
                      onChange={(e) => updateOffset(key, e.target.value)}
                      className="w-20 text-center font-medium bg-white"
                      disabled
                      data-testid={`offset-${key}`}
                    />
                    <span className="text-xs font-medium text-muted-foreground w-12">giorni</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Visibilità Task per Ruolo */}
        <Card className="border border-slate-200 shadow-sm bg-slate-50 overflow-hidden grayscale opacity-70" data-testid="task-visibility-section" aria-disabled="true">
          <CardHeader className="p-6 pb-4 bg-slate-50/50 border-b border-slate-100">
            <CardTitle className="font-heading flex items-center gap-2.5 text-base sm:text-lg">
              <span className="p-2 rounded-lg bg-indigo-50 text-indigo-600 border border-indigo-100/80">
                <UsersIcon className="w-4 h-4" />
              </span>
              Risorse Assegnate alle Task
              <Badge testId="task-visibility-obsoleto">Obsoleto</Badge>
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm mt-1 text-slate-500">
              Seleziona quali ruoli operativi vedono e gestiscono ciascuna tipologia di task. Admin, PM e Resp. Operativo hanno sempre accesso completo.
            </CardDescription>
            <AvvisoObsoleto testId="task-visibility-avviso-obsoleto" />
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="divide-y divide-slate-100 rounded-xl border border-slate-100 bg-slate-50/30 overflow-hidden">
              {Object.entries(TASK_VISIBILITY_LABELS).map(([taskId, label]) => {
                const currentRoles = settings.task_role_visibility?.[taskId] || [];
                return (
                  <div
                    key={taskId}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4"
                  >
                    <div className="flex-1 pr-4">
                      <Label className="text-sm font-semibold text-slate-800">{label}</Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {currentRoles.length === 0
                          ? "Nessun ruolo operativo assegnato"
                          : `Assegnato a: ${currentRoles.map(r => ROLES_OPTIONS.find(o => o.id === r)?.label || r).join(", ")}`}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2 shrink-0">
                      {ROLES_OPTIONS.map(opt => {
                        const checked = currentRoles.includes(opt.id);
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            disabled
                            onClick={() => {
                              const next = checked
                                ? currentRoles.filter(r => r !== opt.id)
                                : [...currentRoles, opt.id];
                              setSettings(prev => ({
                                ...prev,
                                task_role_visibility: {
                                  ...(prev.task_role_visibility || {}),
                                  [taskId]: next
                                }
                              }));
                            }}
                            className={`px-3.5 py-1.5 rounded-full text-xs font-medium border transition-all disabled:cursor-not-allowed ${
                              checked
                                ? "bg-indigo-50 text-indigo-700 border-indigo-300 shadow-sm font-semibold"
                                : "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                            }`}
                            data-testid={`role-${taskId}-${opt.id}`}
                          >
                            {checked ? "✓ " : ""}{opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="p-3 bg-indigo-50/50 border border-indigo-100 rounded-lg">
              <p className="text-xs text-indigo-900 leading-relaxed">
                💡 <strong>Suggerimento:</strong> la stessa task può essere assegnata a più ruoli operativi (ad esempio <em>Script al Cliente</em> può essere gestita sia da SMM che da Content Creator).
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Semaforo Settings */}
        <Card className="border border-slate-200 shadow-sm bg-white overflow-hidden" data-testid="semaforo-section">
          <CardHeader className="p-6 pb-4 bg-slate-50/50 border-b border-slate-100">
            <CardTitle className="font-heading flex items-center gap-2.5 text-base sm:text-lg">
              <span className="p-2 rounded-lg bg-indigo-50 text-indigo-600 border border-indigo-100/80">
                <Settings className="w-4 h-4" />
              </span>
              Soglie Semaforo
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm mt-1 text-slate-500">
              Configura i giorni di anticipo con cui i task passano automaticamente allo stato Giallo (preavviso di scadenza).
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-xl border border-slate-100 bg-slate-50/40">
              <div>
                <Label htmlFor="soglia_giallo" className="text-sm font-semibold text-slate-800">
                  Soglia Preavviso (Giallo)
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Un task passa allo stato Giallo quando mancano questi giorni o meno alla scadenza.
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <Input
                  id="soglia_giallo"
                  type="number"
                  min="1"
                  value={settings.soglia_giallo}
                  onChange={(e) => updateOffset("soglia_giallo", e.target.value)}
                  className="w-20 text-center font-medium bg-white"
                  data-testid="soglia-giallo-input"
                />
                <span className="text-xs font-medium text-muted-foreground w-12">giorni</span>
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
                Legenda stati semaforo
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                <div className="p-4 rounded-xl bg-emerald-50/70 border border-emerald-200/80 flex flex-col items-center justify-center">
                  <div className="w-5 h-5 rounded-full bg-emerald-500 shadow-sm mb-2 ring-4 ring-emerald-100"></div>
                  <p className="text-xs font-bold text-emerald-800">Verde</p>
                  <p className="text-xs text-emerald-600 mt-1 font-medium">&gt; {settings.soglia_giallo} giorni</p>
                </div>
                <div className="p-4 rounded-xl bg-amber-50/70 border border-amber-200/80 flex flex-col items-center justify-center">
                  <div className="w-5 h-5 rounded-full bg-amber-500 shadow-sm mb-2 ring-4 ring-amber-100"></div>
                  <p className="text-xs font-bold text-amber-800">Giallo</p>
                  <p className="text-xs text-amber-600 mt-1 font-medium">1-{settings.soglia_giallo} giorni</p>
                </div>
                <div className="p-4 rounded-xl bg-rose-50/70 border border-rose-200/80 flex flex-col items-center justify-center">
                  <div className="w-5 h-5 rounded-full bg-rose-500 shadow-sm mb-2 ring-4 ring-rose-100"></div>
                  <p className="text-xs font-bold text-rose-800">Rosso</p>
                  <p className="text-xs text-rose-600 mt-1 font-medium">Oggi (scade oggi)</p>
                </div>
                <div className="p-4 rounded-xl bg-slate-100 border border-slate-200 flex flex-col items-center justify-center">
                  <div className="w-5 h-5 rounded-full bg-slate-600 shadow-sm mb-2 ring-4 ring-slate-200"></div>
                  <p className="text-xs font-bold text-slate-800">Scaduto</p>
                  <p className="text-xs text-slate-600 mt-1 font-medium">Data superata</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notification Settings */}
        <Card className="border border-slate-200 shadow-sm bg-white overflow-hidden" data-testid="notifications-section">
          <CardHeader className="p-6 pb-4 bg-slate-50/50 border-b border-slate-100">
            <CardTitle className="font-heading flex items-center gap-2.5 text-base sm:text-lg">
              <span className="p-2 rounded-lg bg-indigo-50 text-indigo-600 border border-indigo-100/80">
                <Bell className="w-4 h-4" />
              </span>
              Notifiche
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm mt-1 text-slate-500">
              Configura i reminder automatici e le notifiche per i task in scadenza e scaduti.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-5">
            <div className="flex items-center justify-between gap-4 p-4 rounded-xl border border-slate-100 bg-slate-50/40">
              <div className="pr-4">
                <Label className="text-sm font-semibold text-slate-800">
                  Reminder Task Scaduti
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Invia una notifica di reminder giornaliera alle ore 09:00 per tutti i task scaduti e non ancora completati.
                </p>
              </div>
              <div className="flex items-center gap-2.5 shrink-0">
                <Switch
                  checked={settings.enable_reminder_scaduto}
                  onCheckedChange={(checked) => 
                    setSettings(prev => ({ ...prev, enable_reminder_scaduto: checked }))
                  }
                  data-testid="toggle-reminder-scaduto"
                />
                <span className="text-xs font-medium text-muted-foreground w-16 text-right">
                  {settings.enable_reminder_scaduto ? "Abilitato" : "Disabilitato"}
                </span>
              </div>
            </div>

            <div className="p-4 bg-slate-50/70 border border-slate-200/70 rounded-xl space-y-3">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                Riepilogo frequenza notifiche
              </h4>
              <ul className="text-xs text-slate-600 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 rounded-full bg-amber-400 mt-1 shrink-0"></span>
                  <span><strong>Giallo:</strong> Inviata quando un task entra in stato Giallo (una tantum).</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 rounded-full bg-rose-500 mt-1 shrink-0"></span>
                  <span><strong>Rosso:</strong> Inviata alle ore 09:00 del giorno esatto della scadenza (una tantum).</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 rounded-full bg-slate-600 mt-1 shrink-0"></span>
                  <span><strong>Scaduto:</strong> Reminder giornaliero alle 09:00 fino al completamento {settings.enable_reminder_scaduto ? "(attivo)" : "(disattivato)"}.</span>
                </li>
              </ul>
              <div className="pt-2 border-t border-slate-200/60">
                <p className="text-xs text-muted-foreground">
                  <strong>Destinatari:</strong> Ruolo operativo assegnato + Project Manager sempre in copia.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Catalogo Servizi — solo admin/direttore commerciale */}
        {canManageCatalogoServizi && (
          <div className="pt-4">
            <CatalogoServiziPanel />
          </div>
        )}
        {canManageCatalogoServizi && (
          <div className="pt-4">
            <CatalogoServiziPanel ambito="eventi" />
          </div>
        )}
      </div>
    </div>
  );
}
